import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpModule, XHRBackend, RequestOptions } from '@angular/http';

import { AlertModule } from 'ngx-bootstrap/alert';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { Angular2FontawesomeModule } from 'angular2-fontawesome/angular2-fontawesome';
import { NgxMyDatePickerModule } from 'ngx-mydatepicker';
import { Select2Module } from 'ng2-select2';
import { MomentModule } from 'angular2-moment';
import { InfiniteScrollModule } from 'angular2-infinite-scroll';
import { InterceptorService } from 'ng2-interceptors';
import { AuthInterceptor } from './security/auth-interceptor.service';
import { AppRoutingModule } from './shared/app-routing.module';

import { AppComponent } from './app.component';
import { UserComponent } from './user/user.component';
import { AccountComponent } from './account/account.component';
import { HomeComponent } from './home/home.component';
import { LeftMenuComponent } from './left-menu/left-menu.component';
import { AnalysisComponent } from './analysis/analysis.component';
import { CancellationComponent } from './cancellation/cancellation.component';
import { CancellationListComponent } from './cancellation/cancellation-list.component';
import { SubscriptionComponent } from './subscription/subscription.component';
import { OrderComponent } from './order/order.component';
import { FastOrderComponent } from './order/fast-process/fast-order.component';
import { ProductSelectorComponent } from './order/product-selector/product-selector.component';
import { PackageSelectorComponent } from './order/package-selector/package-selector.component';
import { CustomerInfosComponent } from './customer-infos/customer-infos.component';
import { RelationSearchComponent } from './customer-infos/relation-search/relation-search.component';
import { RelationSearchService } from './customer-infos/relation-search/relation-search.service';
import { CustomerInfosOrderComponent } from './customer-infos/customer-infos-order.component';
import { LoginComponent } from './login/login.component';
import { BypassAuthComponent } from './bypassAuth/bypass-auth.component';
import { SignupComponent } from './signup/signup.component';
import { SpinnerComponent } from './shared/spinner/spinner.component';
import { AlertComponent } from './shared/alert/alert.component';

import { AnalysisResolver } from './resolvers/analysis.resolver';
import { CancellationResolver } from './resolvers/cancellation.resolver';
import { CancellationListResolver } from './resolvers/cancellation-list.resolver';
import { OrderResolver } from './resolvers/order.resolver';
import { FastOrderResolver } from './resolvers/fast-order.resolver';
import { EditOrderResolver } from './resolvers/edit-order.resolver';
import { SiretLabsResolver } from './resolvers/siret-lab.resolver';
import { RequestStatusResolver } from './resolvers/request-status.resolver';
import { SubscriptionResolver } from './resolvers/subscription.resolver';
import { CustomerInfosResolver } from './resolvers/customer-infos.resolver';
import { CustomerInfosOrderResolver } from './resolvers/customer-infos-order.resolver';

import { SignupService } from './signup/signup.service';
import { AnalysisService } from './analysis/analysis.service';
import { CancellationService } from './cancellation/cancellation.service';
import { OrderService } from './order/order.service';
import { SubscriptionService } from './subscription/subscription.service';
import { CustomerInfosService } from './customer-infos/customer-infos.service';
import { AdditionalInformationListComponent } from './shared/analysis/additional-information/additional-information-list/additional-information-list.component';
import { AdditionalInformationInputComponent } from './shared/analysis/additional-information/additional-information-input/additional-information-input.component';
import { AdditionalInformationService } from './shared/analysis/additional-information/additional-information.service';
import { SpinnerService } from './shared/spinner/spinner.service';
import { SnakeCasePipe } from './shared/pipes/snake-case.pipe';
import { CamelCasePipe } from './shared/pipes/camel-case.pipe';
import { ResultsFilterPipe } from './shared/pipes/results-filter.pipe';
import { SortResultsFilterPipe } from './shared/pipes/sort-results.pipe';
import { OrdersFilterPipe } from './shared/pipes/orders-filter.pipe';
import { OrdersInfosFilterPipe } from './shared/pipes/orders-infos-filter.pipe';
import { NotCancelledResultsFilterPipe } from './shared/pipes/not-cancelled-results-filter.pipe';
import { NotExcludedResultsFilterPipe } from './shared/pipes/not-excluded-results-filter.pipe';
import { ProductFilterPipe } from './shared/pipes/product-filter.pipe';
import { ThirdPartyFilterPipe } from './shared/pipes/third-party-filter.pipe';
import { PackageFilterPipe } from './shared/pipes/package-filter.pipe';
import { PackageClassFilterPipe } from './shared/pipes/package-class-filter.pipe';
import { AlertService } from './shared/alert/alert.service';
import { UploadService } from './shared/upload.service';
import { LocalStorageService } from './shared/local-storage.service';
import { AutoCompleteService } from './shared/auto-complete.service';
import { WsseService } from './security/wsse.service';
import { AuthenticationService } from './security/authentication.service';
import { AuthGuard } from './security/auth.guard';
import { IsDairyGuard } from './security/is-dairy.guard';
import { IsNotDairyGuard } from './security/is-not-dairy.guard';
import { ProducersAutocompleteService } from './shared/producers/producers-autocomplete.service';
import { ProducersAutocompleteComponent } from './shared/producers/producers-autocomplete.component';
import { FileUploadComponent } from './shared/file-upload/file-upload.component';
import { FileUploadService } from './shared/file-upload/file-upload.service';
import { AdditionalDocsComponent } from './additional-docs/additional-docs.component';
import { AdditionalDocResolver } from './resolvers/additional-doc.resolver';
import { AdditionalDocsService } from './additional-docs/additional-docs.service';
import { OrderLastCommentComponent } from './shared/orderLastComment/order-last-comment.component';
import { ForgottenPasswordComponent } from './forgottenPassword/forgotten-password.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { BlobService } from './shared/blob.service';
import { CommentsComponent } from './comments/comments.component';
import { CommentsService } from './comments/comments.service';


export function interceptorFactory(xhrBackend: XHRBackend, requestOptions: RequestOptions, authInterceptor: AuthInterceptor){ // Add it here
  let service = new InterceptorService(xhrBackend, requestOptions);
  service.addInterceptor(authInterceptor); // Add it here
  return service;
}

@NgModule({
  // Define other components in our module
  declarations: [
    AppComponent,
    HomeComponent,
    LeftMenuComponent,
    UserComponent,
    AccountComponent,
    AnalysisComponent,
    CancellationComponent,
    CancellationListComponent,
    SubscriptionComponent,
    OrderComponent,
    FastOrderComponent,
    CustomerInfosComponent,
    RelationSearchComponent,
    CustomerInfosOrderComponent,
    ProductSelectorComponent,
    PackageSelectorComponent,
    LoginComponent,
    ForgottenPasswordComponent,
    ResetPasswordComponent,
    BypassAuthComponent,
    SignupComponent,
    SpinnerComponent,
    AlertComponent,
    CamelCasePipe,
    SnakeCasePipe,
    ResultsFilterPipe,
    SortResultsFilterPipe,
    OrdersFilterPipe,
    OrdersInfosFilterPipe,
    NotCancelledResultsFilterPipe,
    NotExcludedResultsFilterPipe,
    ProductFilterPipe,
    PackageFilterPipe,
    PackageClassFilterPipe,
    ThirdPartyFilterPipe,
    AdditionalInformationListComponent,
    AdditionalInformationInputComponent,
    AdditionalDocsComponent,
    FileUploadComponent,
    ProducersAutocompleteComponent,
    OrderLastCommentComponent,
    CommentsComponent
  ],
  exports: [
      AdditionalInformationListComponent,
      AdditionalInformationInputComponent,
      FileUploadComponent,
      ProducersAutocompleteComponent
  ],
  // Define the services imported by our app
  imports: [
    AlertModule.forRoot(),
    CollapseModule.forRoot(),
    ModalModule.forRoot(),
    TypeaheadModule.forRoot(),
    BsDropdownModule.forRoot(),
    Angular2FontawesomeModule,
    Select2Module,
    NgxMyDatePickerModule,
    MomentModule,
    BrowserModule,
    FormsModule,
    HttpModule,
    InfiniteScrollModule,
    AppRoutingModule,
    ReactiveFormsModule
  ],
  providers: [
    AdditionalDocsService,
    SpinnerService,
    SignupService,
    AnalysisService,
    AnalysisResolver,
    AdditionalDocResolver,
    CancellationService,
    OrderService,
    CustomerInfosService,
    CustomerInfosResolver,
    CustomerInfosOrderResolver,
    RelationSearchService,
    CancellationResolver,
    CancellationListResolver,
    OrderResolver,
    FastOrderResolver,
    EditOrderResolver,
    SiretLabsResolver,
    RequestStatusResolver,
    SubscriptionResolver,
    SubscriptionService,
    AlertService,
    UploadService,
    LocalStorageService,
    AutoCompleteService,
    AdditionalInformationService,
    WsseService,
    AuthenticationService,
    ProducersAutocompleteService,
    BlobService,
    AuthInterceptor,
    FileUploadService,
    AuthGuard,
    IsDairyGuard,
    IsNotDairyGuard,
    CommentsService,
    {
      provide: InterceptorService,
      useFactory: interceptorFactory,
      deps: [XHRBackend, RequestOptions, AuthInterceptor]
    }
  ],
  // Define the root component
  bootstrap: [AppComponent],
  entryComponents:[CommentsComponent]
})
export class AppModule { }



// WEBPACK FOOTER //
// ./src/app/app.module.ts